/**
 * Auth hook — Sprint 2 F5.
 *
 * Reads auth state from localStorage (which is how V2 stored it).
 * Provides logout + current user/concessionaire info.
 */
import { useCallback, useEffect, useState } from 'react';
import egoalLogo from '../../../assets/images/logo/egoallogo.png';
import { suppressSessionExpiry } from './sessionExpired';

function readAuthFromStorage() {
    return {
        token: localStorage.getItem('sessiontoken') || '',
        refreshToken: localStorage.getItem('refresh_token') || '',
        userId: localStorage.getItem('loginId') || '',
        userRole: localStorage.getItem('usertype') || '',
        userName: localStorage.getItem('loginUsername') || '',
        firstName: localStorage.getItem('loginName') || '',
        concessionaireId: localStorage.getItem('concessionaireId') || '',
        concessionaireName: localStorage.getItem('concessionaireName') || '',
        propertyAccess: localStorage.getItem('propertyAccess') || '',
        isAuthenticated: !!localStorage.getItem('sessiontoken'),
    };
}

export function useAuth() {
    const [auth, setAuth] = useState(readAuthFromStorage);

    // Re-read when storage changes (e.g. after login)
    useEffect(() => {
        const handler = () => setAuth(readAuthFromStorage());
        window.addEventListener('storage', handler);
        return () => window.removeEventListener('storage', handler);
    }, []);

    const logout = useCallback(() => {
        // Intentional sign-out — latch the session-expiry handler closed so the
        // 401s from clearing the token below don't pop the "Session has ended" alert.
        suppressSessionExpiry();

        // Show a minimal, branded loader during the sign-out transition (same
        // visual language as the Spatial loader) instead of a frozen page / flash.
        try {
            if (!document.querySelector('.v3-logout-loader')) {
                const o = document.createElement('div');
                o.className = 'v3-logout-loader';
                o.innerHTML =
                    '<img class="lo-img" src="' + egoalLogo + '" alt="EGoal" />' +
                    '<div class="lo-line"><div class="lo-fill"></div></div>' +
                    '<div class="lo-title"><strong>Signing out</strong> of EGoal</div>';
                document.body.appendChild(o);
            }
        } catch (_) { /* no-op */ }

        localStorage.clear();
        setAuth(readAuthFromStorage());
        // Hold the logo on screen for a beat (Gmail-style) before the hard nav.
        setTimeout(() => { window.location.href = '/login'; }, 1600);
    }, []);

    return { ...auth, logout, refresh: () => setAuth(readAuthFromStorage()) };
}
